import React, { useState, useMemo } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ICON_CATEGORIES, ALL_ICONS_UNIQUE } from './icon-categories';
import type { IconItem } from './icon-categories';

interface IconPickerProps {
  value: string;
  onChange: (value: string) => void;
  /** Which category key to show by default (default: 'assets') */
  defaultCategory?: string;
}

const IconButton: React.FC<{
  icon: IconItem;
  isSelected: boolean;
  onClick: () => void;
}> = ({ icon, isSelected, onClick }) => (
  <button
    key={icon.value}
    type="button"
    onClick={onClick}
    className={`flex items-center gap-1 px-2 py-1 rounded border text-xs transition-all ${
      isSelected
        ? 'bg-moss-primary text-white border-moss-primary'
        : 'bg-white text-neutral-700 border-neutral-200 hover:border-moss-primary hover:bg-moss-primary/5'
    }`}
    title={icon.label}
  >
    <icon.Icon weight="duotone" className="h-3.5 w-3.5" />
    <span>{icon.label}</span>
  </button>
);

const IconCategorySection: React.FC<{
  label: string;
  icons: IconItem[];
  selectedValue: string;
  onSelect: (value: string) => void;
  collapsible?: boolean;
}> = ({ label, icons, selectedValue, onSelect, collapsible = false }) => {
  const [expanded, setExpanded] = useState(false);
  // 1 row ≈ 34px (button height 28px + gap 6px)
  const isCollapsed = collapsible && !expanded;

  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-2">{label}</p>
      <div
        className={`flex flex-wrap gap-1.5 ${isCollapsed ? 'max-h-[30px] overflow-hidden' : ''}`}
      >
        {icons.map((icon) => (
          <IconButton
            key={icon.value}
            icon={icon}
            isSelected={selectedValue === icon.value}
            onClick={() => onSelect(selectedValue === icon.value ? '' : icon.value)}
          />
        ))}
      </div>
      {collapsible && icons.length > 6 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-moss-primary hover:underline mt-1"
        >
          {expanded ? 'Minder tonen' : `+${icons.length - 6} meer...`}
        </button>
      )}
    </div>
  );
};

export const IconPicker: React.FC<IconPickerProps> = ({ value, onChange, defaultCategory = 'assets' }) => {
  const [showMoreIcons, setShowMoreIcons] = useState(false);
  const [iconSearch, setIconSearch] = useState('');

  const searchResults = useMemo(() => {
    if (!iconSearch.trim()) return [];
    const term = iconSearch.toLowerCase();
    return ALL_ICONS_UNIQUE.filter(
      ({ value: v, label }) =>
        label.toLowerCase().includes(term) || v.toLowerCase().includes(term),
    );
  }, [iconSearch]);

  const defaultCat = ICON_CATEGORIES.find((c) => c.key === defaultCategory) || ICON_CATEGORIES[0];
  const additionalCategories = ICON_CATEGORIES.filter((c) => c.key !== defaultCategory);

  return (
    <div className="space-y-2">
      <Label>Icoon</Label>
      <div className="border rounded-lg bg-muted/20 p-3 space-y-3">
        {/* Search bar */}
        <div className="relative">
          <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Zoek icoon..."
            value={iconSearch}
            onChange={(e) => setIconSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>

        {iconSearch.trim() ? (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">
              Zoekresultaten ({searchResults.length})
            </p>
            <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
              {searchResults.map((icon) => (
                <IconButton
                  key={icon.value}
                  icon={icon}
                  isSelected={value === icon.value}
                  onClick={() => onChange(value === icon.value ? '' : icon.value)}
                />
              ))}
              {searchResults.length === 0 && (
                <p className="text-xs text-muted-foreground italic py-2">
                  Geen iconen gevonden
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            <IconCategorySection
              label={defaultCat.label}
              icons={defaultCat.icons}
              selectedValue={value}
              onSelect={onChange}
              collapsible
            />

            {showMoreIcons ? (
              <>
                {additionalCategories.map((cat) => (
                  <IconCategorySection
                    key={cat.key}
                    label={cat.label}
                    icons={cat.icons}
                    selectedValue={value}
                    onSelect={onChange}
                    collapsible
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setShowMoreIcons(false)}
                  className="text-xs text-moss-primary hover:underline"
                >
                  Minder iconen tonen
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setShowMoreIcons(true)}
                className="text-xs text-moss-primary hover:underline"
              >
                Meer iconen tonen (+{ALL_ICONS_UNIQUE.length - defaultCat.icons.length} iconen
                in {additionalCategories.length} categorieën)
              </button>
            )}
          </>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Kies een icoon dat bij de categorie past (optioneel) - {ALL_ICONS_UNIQUE.length} iconen
        beschikbaar
      </p>
    </div>
  );
};
